import { DoubleSide, Mesh, MeshBasicMaterial, Path, Shape, ShapeGeometry, Vector2 } from 'three';
import { DIRECTION } from '../../../js/structures/aqua/gridAqua';
import { ContornoGroup } from './sceneGroupConfig';

//helper class to be able to draw a tent for shape
export class LineUtils {

	static #instance = null;
	static get Instance() {
		if( !LineUtils.#instance ) LineUtils.#instance = new LineUtils();
		return this.#instance;
	}

	createGridShape( grid ) {
		const gridShape = new ContornoGroup();
		const shapes = this.#createOuterLines( grid );
		for( let i = 0; i < shapes.length; i++ ) {
			const shape = shapes[i];

			// Define the geometry from the shape
			const s = new Shape( shape.line );
			s.holes = shape.holes;
			const geometry = new ShapeGeometry( s );

			// Create a semi transparent grey material
			const material = new MeshBasicMaterial( { color: 0x000000, side: DoubleSide, transparent: true, opacity: 0.5 } );

			// Create a mesh from the geometry and material
			const mesh = new Mesh(geometry, material);
			mesh.rotateOnAxis( { x: 1, y: 0, z: 0 }, -Math.PI / 2 );

			// Add the mesh to the scene
			gridShape.add( mesh );
		}

		return gridShape;
	}
	
	#createOuterLines( grid ) {
		//get frontals. Each frontal must have a continuous lateral, until all frontals are used
		const frontals = grid.frontales;
		const usedFrontals = [];
		const usedLaterals = [];
		let i = 0;
		const lines = [];
		while( usedFrontals.length !== frontals.length ) {
			const frontal = grid.frontales[i]; i++;
			const used = usedFrontals.find( f => f === frontal );
			if( used ) continue;
			//It is not used, so we make the contour
			const data = {
				frontales: [],
				laterales: [],
				points: []
			};
			this.#drawContorno( data, frontal, 'right', grid, usedFrontals, usedLaterals );

			//check if the data is a hole or not. If the maximun north side is lower then the maximun south side, then is a hole
			const norths = data.frontales.filter( f => f.direction === DIRECTION.NORTH );
			const souths = data.frontales.filter( f => f.direction === DIRECTION.SOUTH );
			const northMax = Math.max( ...norths.map( n => n.malla ) );
			const southMax = Math.max( ...souths.map( s => s.malla ) );
			data.isHole = northMax > southMax;

			lines.push( data );			
		}

		return this.#categorizeLines( lines );
	}

	#categorizeLines( lines ) {
		
		const result = [];
		const holes = [];

		// Helpers to get bounding box from points array and to see if a box is inside another box
		const getBBox = points => {
			let minX = Infinity;
			let minY = Infinity;
			let maxX = -Infinity;
			let maxY = -Infinity;
			for( let i = 0; i < points.length; i++ ) {
				const p = points[i];
				minX = Math.min( minX, p.x );
				minY = Math.min( minY, p.y );
				maxX = Math.max( maxX, p.x );
				maxY = Math.max( maxY, p.y );
			}
			
			return { 
				min: { x: minX, y: minY },
				max: { x: maxX, y: maxY }
			};
		};
		const isInside = ( box1, box2 ) => {
			return box1.min.x > box2.min.x && box1.max.x < box2.max.x &&
				box1.min.y > box2.min.y && box1.max.y < box2.max.y;
		};
				

		// prepare data. Set bounding boxes, holes arrays and if it is a hole or not
		for( let i = 0; i < lines.length; i++ ) {
			const data = lines[i];
			data.box = getBBox( data.points );

			if( !data.isHole ) result.push( {
				line: data.points,
				holes: [],
				data: data
			} );
			else holes.push( data );
		}

		//we need to add all the holes in their respective
		for( let i = 0; i < holes.length; i++ ) {
			const data = holes[i];
			for( let j = 0; j < result.length; j++ ) {
				const r = result[j];
				if( isInside( data.box, r.data.box ) ) {
					r.holes.push( new Path( data.points ) );
					break;
				}
			}
		}

		return result;
	}

	#drawContorno( data, frontal, dir, grid, usedFrontals, usedLaterals ) {
		
		//if frontal is already used break the loop
		if( usedFrontals.find( f => f === frontal ) ) return data;

		this.#drawContornoFrontal( data, frontal, dir, grid, usedFrontals, usedLaterals );
	}

	#drawContornoFrontal( data, frontal, dir, grid, usedFrontals, usedLaterals ) {
		//if frontal is already used break the loop
		if( usedFrontals.find( f => f === frontal ) ) return data;

		// helpers methods
		const getPoints = ( cells ) => {
			const firstCell = cells[0];
			const lastCell = cells[cells.length - 1];
			const initPoint = new Vector2( firstCell.position.x, -firstCell.position.z );
			const lastPoint = new Vector2( lastCell.position.x, -lastCell.position.z );
			return { 
				init: { point: initPoint, cell: firstCell },
				last: { point: lastPoint, cell: lastCell }
			};			
		};
		const hasPoint = ( list, point ) => {
			const margen = 0.1;
			const found = list.find( p => Math.abs( p.x - point.x ) < margen && Math.abs( p.y - point.y ) < margen );
			return found ? true : false;
		};

		const sortedCells = dir === 'right' ? frontal.cells.sort( ( a, b ) => a.MIDColumna - b.MIDColumna ) : 
			frontal.cells.sort( ( a, b ) => b.MIDColumna - a.MIDColumna );
		
		let ps = getPoints( sortedCells );

		// move vertically the points
		if( frontal.direction === DIRECTION.NORTH ) {
			ps.init.point.y += ps.init.cell.MIDLargo / 2;
			ps.last.point.y += ps.last.cell.MIDLargo / 2;
		} else {
			ps.init.point.y -= ps.init.cell.MIDLargo / 2;
			ps.last.point.y -= ps.last.cell.MIDLargo / 2;
		}
		// move horizontally the points to the corners
		ps.init.point.x += dir === 'right' ? -ps.init.cell.MIDAncho / 2 : ps.init.cell.MIDAncho / 2;
		ps.last.point.x += dir === 'right' ? ps.last.cell.MIDAncho / 2 : -ps.last.cell.MIDAncho / 2;

		if( !hasPoint( data.points, ps.init.point ) ) data.points.push( ps.init.point );
		data.points.push( ps.last.point );
		
		usedFrontals.push( frontal );
		data.frontales.push( frontal );

		//check if there is a next frontal
		const nextRightFrontal = grid.frontales.find( f => !usedFrontals.find( uf => uf === f ) && 
				f.direction === frontal.direction &&
				f.malla === frontal.malla && f.inicio === ps.last.cell.MIDColumna + 1 );
		if( nextRightFrontal ) this.#drawContornoFrontal( data, nextRightFrontal, 'right', grid, usedFrontals, usedLaterals );
		else {
			const nextLeftFrontal = grid.frontales.find( f => !usedFrontals.find( uf => uf === f ) && 
				f.direction === frontal.direction &&
				f.malla === frontal.malla && f.fin === ps.last.cell.MIDColumna - 1 );
			if( nextLeftFrontal ) this.#drawContornoFrontal( data, nextLeftFrontal, 'left', grid, usedFrontals, usedLaterals );
		
			else {
				// there should be a lateral
				this.#drawContornoLateral( data, frontal, ps, grid, usedFrontals, usedLaterals );
			}
		}
	}

	#drawContornoLateral( data, frontal, ps, grid, usedFrontals, usedLaterals ) {

		// helpers methods
		const getPoints = ( cells ) => {
			const firstCell = cells[0];
			const lastCell = cells[cells.length - 1];
			const initPoint = new Vector2( firstCell.position.x, -firstCell.position.z );
			const lastPoint = new Vector2( lastCell.position.x, -lastCell.position.z );
			return { 
				init: { point: initPoint, cell: firstCell },
				last: { point: lastPoint, cell: lastCell }
			};			
		};
		const hasPoint = ( list, point ) => {
			const margen = 0.1;
			const found = list.find( p => Math.abs( p.x - point.x ) < margen && Math.abs( p.y - point.y ) < margen );
			return found ? true : false;
		};
		const isLateralGoingDown = ( frontal, lateral ) => {
			//sort from top to down first
			return frontal.direction === DIRECTION.NORTH ?
				frontal.malla <= lateral.inicio && frontal.malla <= lateral.fin : 
				frontal.malla < lateral.inicio && frontal.malla < lateral.fin;
		} ;

		const addLateralPoints = ( lateral ) => {
			const isGoingDown = isLateralGoingDown( frontal, lateral );
			const sortedLatCells = isGoingDown ? lateral.cells.sort( ( a, b ) => a.MIDFila - b.MIDFila ) :
				lateral.cells.sort( ( a, b ) => b.MIDFila - a.MIDFila );
		
			const ps = getPoints( sortedLatCells );

			// move horizontally the points
			if( lateral.direction === DIRECTION.EAST ) {
				ps.init.point.x += ps.init.cell.MIDAncho / 2;
				ps.last.point.x += ps.last.cell.MIDAncho / 2;
			} else {
				ps.init.point.x -= ps.init.cell.MIDAncho / 2;
				ps.last.point.x -= ps.last.cell.MIDAncho / 2;
			}
			// move vertically the points to the corners
			ps.init.point.y += isGoingDown ? ps.init.cell.MIDLargo / 2 : -ps.init.cell.MIDLargo / 2;
			ps.last.point.y += isGoingDown ? -ps.last.cell.MIDLargo / 2 : ps.last.cell.MIDLargo / 2;

			if( !hasPoint( data.points, ps.init.point ) ) data.points.push( ps.init.point );
			data.points.push( ps.last.point );

			return { lastCell: sortedLatCells[sortedLatCells.length - 1], ps: ps, isGoingDown: isGoingDown };
		}; 

		// there should be a lateral
		const lateralData = this.#getNextLateral( grid, frontal, ps.last.cell, ps.last.point.x, usedLaterals );
		if( !lateralData.lateral ) return data;

		let lateral = lateralData.lateral;
		let lastlateral = null;
		let v = null;
		while ( lateral ) {
			lastlateral = lateral;
			usedLaterals.push( lateral );
			data.laterales.push( lateral );

			v = addLateralPoints( lateral );
		
			//check if there is a next lateral
			lateral = grid.laterales.find( l => !usedLaterals.find( ul => ul === l ) &&
						Math.abs( l.linePos - lateral.linePos ) < 0.1 &&
						l.capilla === v.lastCell.MIDColumna &&
						( v.isGoingDown ? v.lastCell.MIDFila + 1 === l.fin : v.lastCell.MIDFila - 1 === l.inicio ) 
			);
		}
		
		//find the next frontal
		const nextFrontal = this.#getNextFrontal( grid, lastlateral, v.lastCell, -v.ps.last.point.y, usedFrontals );
		if( nextFrontal.frontal ) this.#drawContornoFrontal( data, nextFrontal.frontal, nextFrontal.dir, grid, usedFrontals, usedLaterals );				
	}

	#getNextLateral( grid, frontal, lastCell, linePos, usedLaterals ) {
		//find the next lateral
		const findLateral = ( row, col ) => grid.laterales.find( l => !usedLaterals.find( ul => ul === l ) &&
			Math.abs( l.linePos - linePos ) < 0.1 &&
			l.capilla === col && ( l.inicio === row || l.fin === row ) );
		
		let lateral = null;
		if( frontal.direction === DIRECTION.SOUTH ) {

			lateral = findLateral( lastCell.MIDFila + 1, lastCell.MIDColumna - 1 );
			if( lateral ) return { lateral: lateral, dir: 'down' };

			lateral = findLateral( lastCell.MIDFila + 1, lastCell.MIDColumna + 1 );
			if( lateral ) return { lateral: lateral, dir: 'down' };

			lateral = findLateral( lastCell.MIDFila, lastCell.MIDColumna );
			if( lateral ) return { lateral: lateral, dir: 'up' };
		} else {

			lateral = findLateral( lastCell.MIDFila - 1, lastCell.MIDColumna + 1 );
			if( lateral ) return { lateral: lateral, dir: 'up' };

			lateral = findLateral( lastCell.MIDFila - 1, lastCell.MIDColumna - 1 );
			if( lateral ) return { lateral: lateral, dir: 'up' };

			lateral = findLateral( lastCell.MIDFila, lastCell.MIDColumna );
			if( lateral ) return { lateral: lateral, dir: 'down' };
		}

		return { lateral: null, dir: null };
	}

	#getNextFrontal( grid, lateral, lastCell, linePos, usedFrontals ) {
		//find the next frontal
		const findFrontal = ( row, col ) => grid.frontales.find( f => !usedFrontals.find( uf => uf === f ) &&
			Math.abs( f.linePos - linePos ) < 0.1 &&
			f.malla === row && ( f.inicio === col || f.fin === col ) ); 

		let frontal = null;
		if( lateral.direction === DIRECTION.WEST ) {
			
			frontal = findFrontal( lastCell.MIDFila - 1, lastCell.MIDColumna - 1 );
			if( frontal ) return { frontal: frontal, dir: 'left' };

			frontal = findFrontal( lastCell.MIDFila + 1, lastCell.MIDColumna - 1 );
			if( frontal ) return { frontal: frontal, dir: 'left' };

			frontal = findFrontal( lastCell.MIDFila, lastCell.MIDColumna );
			if( frontal ) return { frontal: frontal, dir: 'right' };
			
		} else {
			
			frontal = findFrontal( lastCell.MIDFila - 1, lastCell.MIDColumna + 1 );
			if( frontal ) return { frontal: frontal, dir: 'right' };

			frontal = findFrontal( lastCell.MIDFila + 1, lastCell.MIDColumna + 1 );
			if( frontal ) return { frontal: frontal, dir: 'right' };

			frontal = findFrontal( lastCell.MIDFila, lastCell.MIDColumna );
			if( frontal ) return { frontal: frontal, dir: 'left' };
		}

		return { frontal: null, dir: null };
	}

}

